import { useState, useRef, useEffect, useCallback } from 'react';
import { Bell, Check, CheckCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  metadata: any;
  isRead: boolean;
  createdAt: string;
}

function timeAgo(date: string): string {
  const diff = Date.now() - new Date(date).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

const TYPE_ICONS: Record<string, string> = {
  lead_assigned: 'bg-indigo-100 text-indigo-600',
  lead_status_changed: 'bg-amber-100 text-amber-600',
  task_assigned: 'bg-emerald-100 text-emerald-600',
  new_booking: 'bg-pink-100 text-pink-600',
};

export function NotificationBell() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  // IDs the user clicked locally — keeps them "read" in UI even if the
  // server PATCH failed and the next poll still returns them as unread.
  // No revert: optimistic update wins.
  const locallyReadIds = useRef<Set<string>>(new Set());
  const allReadLocally = useRef(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const fetchUnreadCount = useCallback(() => {
    if (!user) return;
    api.get('/notifications/unread-count').then(r => {
      const serverCount: number = r.data.count ?? 0;
      // Subtract locally-read IDs that the server still thinks are unread.
      // If user clicked "mark all read", show 0 regardless until next page reload.
      if (allReadLocally.current) {
        setUnreadCount(0);
      } else {
        // Best-effort adjustment — server doesn't tell us which specific
        // ids are unread on this poll, so we just subtract the local count.
        // Clamped at 0.
        setUnreadCount(Math.max(0, serverCount - locallyReadIds.current.size));
      }
    }).catch(() => {});
  }, [user]);

  const fetchNotifications = useCallback(() => {
    setLoading(true);
    api.get('/notifications?page=1&limit=15')
      .then(r => {
        const list: Notification[] = r.data.data ?? [];
        // Apply local optimistic-read state on top of server response.
        const merged = list.map(n =>
          allReadLocally.current || locallyReadIds.current.has(n.id)
            ? { ...n, isRead: true }
            : n,
        );
        setNotifications(merged);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Poll unread count every 30s + on tab focus
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    const onFocus = () => fetchUnreadCount();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(interval); window.removeEventListener('focus', onFocus); };
  }, [fetchUnreadCount]);

  // Load notifications when dropdown opens
  useEffect(() => {
    if (open) fetchNotifications();
  }, [open, fetchNotifications]);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const markRead = (id: string) => {
    // Optimistic update immediately + lock the id so future polls can't
    // resurrect the red dot. API is best-effort; we don't revert on failure.
    locallyReadIds.current.add(id);
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, isRead: true } : n));
    setUnreadCount(c => Math.max(0, c - 1));
    api.patch(`/notifications/${id}/read`).catch(() => {});
  };

  const markAllRead = () => {
    allReadLocally.current = true;
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    setUnreadCount(0);
    api.patch('/notifications/read-all').catch(() => {});
  };

  const handleClick = (n: Notification) => {
    if (!n.isRead) markRead(n.id);
    setOpen(false);
    if (n.metadata?.ticketId) navigate(`/support/${n.metadata.ticketId}`);
    else if (n.metadata?.leadId) navigate(`/leads/${n.metadata.leadId}`);
    else if (n.type === 'task_assigned') navigate('/tasks');
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(v => !v)}
        className="relative h-9 w-9 flex items-center justify-center rounded-full text-[#64748B] hover:text-[#6366F1] hover:bg-indigo-50 transition-colors"
        title="Notifications"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 h-4.5 w-4.5 min-w-[18px] flex items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white px-1">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-11 w-[360px] bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden"
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <Bell className="h-4 w-4 text-[#6366F1]" />
              <span className="text-sm font-semibold text-[#1E293B]">Notifications</span>
              {unreadCount > 0 && (
                <span className="text-[10px] font-bold text-white bg-red-500 rounded-full px-1.5 py-0.5">{unreadCount}</span>
              )}
            </div>
            {unreadCount > 0 && (
              <button onClick={markAllRead} className="text-xs text-[#6366F1] hover:text-[#4F46E5] font-medium flex items-center gap-1">
                <CheckCheck className="h-3.5 w-3.5" /> Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div className="max-h-[400px] overflow-y-auto">
            {loading ? (
              <div className="py-10 text-center">
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-[#6366F1] border-t-transparent mx-auto" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="py-10 text-center">
                <Bell className="h-8 w-8 text-gray-200 mx-auto mb-2" />
                <p className="text-sm text-[#64748B]">No notifications yet</p>
              </div>
            ) : (
              notifications.map(n => (
                <button
                  key={n.id}
                  onClick={() => handleClick(n)}
                  className={`w-full text-left flex items-start gap-3 px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-50 ${!n.isRead ? 'bg-indigo-50/30' : ''}`}
                >
                  <div className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${TYPE_ICONS[n.type] || 'bg-slate-100 text-slate-500'}`}>
                    <Bell className="h-3.5 w-3.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-medium truncate ${!n.isRead ? 'text-[#1E293B]' : 'text-slate-600'}`}>{n.title}</span>
                      {!n.isRead && <span className="h-2 w-2 rounded-full bg-[#6366F1] flex-shrink-0" />}
                    </div>
                    <p className="text-xs text-slate-500 truncate mt-0.5">{n.message}</p>
                    <p className="text-[10px] text-slate-400 mt-1">{timeAgo(n.createdAt)}</p>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
